import { Transaction, Document } from "./dndTypes.ts";
import { RenderTypeEnum } from "./types.ts";

export type TemporaryAttachment =
    | { kind: 'document'; document_id: string; host_id: string }
    | { kind: 'transaction'; transaction_id: string; host_id: string };

function guestId(attachment: TemporaryAttachment): string {
    return attachment.kind === 'document' ? attachment.document_id : attachment.transaction_id;
}

export interface DndState {
    transactions: Transaction[];
    documents: Document[];
    temporaryAttachments: TemporaryAttachment[];
    activeDragId: string | null;
}

export type DndAction =
    | { type: 'SYNC_BACKEND_DATA'; payload: { transactions: Transaction[]; documents: Document[] } }
    | { type: 'ATTACH_ITEM'; payload: TemporaryAttachment }
    | { type: 'DETACH_ITEM'; payload: { hostId: string; guestId: string } }
    | { type: 'CLEAR_TEMPORARY_ATTACHMENTS'; payload?: { hostId?: string } }
    | { type: 'SET_ACTIVE_DRAG'; payload: string | null };

export function initialState(transactions: Transaction[], documents: Document[]): DndState {
    return {
        transactions,
        documents,
        temporaryAttachments: [],
        activeDragId: null,
    };
}

export function dndReducer(state: DndState, action: DndAction): DndState {
    switch (action.type) {
        case 'SYNC_BACKEND_DATA': {
            const { transactions, documents } = action.payload;

            const txIds = new Set(transactions.map(t => t.transaction_id));
            const docIds = new Set(
                documents
                    .filter(d => d.render_type !== RenderTypeEnum.PARTIAL_MATCH)
                    .map(d => (d as { document_id: string }).document_id)
            );
            const matchIds = new Set(
                documents
                    .filter(d => d.render_type === RenderTypeEnum.PARTIAL_MATCH)
                    .map(d => (d as { match_id: string }).match_id)
            );

            const cleaned = state.temporaryAttachments.filter(a => {
                const hostExists = txIds.has(a.host_id) || docIds.has(a.host_id) || matchIds.has(a.host_id);
                const guestExists = a.kind === 'document'
                    ? docIds.has(a.document_id)
                    : txIds.has(a.transaction_id);
                return hostExists && guestExists;
            });

            return {
                ...state,
                transactions,
                documents,
                temporaryAttachments: cleaned,
            };
        }

        case 'ATTACH_ITEM': {
            const attachment = action.payload;
            const gid = guestId(attachment);

            const exists = state.temporaryAttachments.some(
                a => a.host_id === attachment.host_id && a.kind === attachment.kind && guestId(a) === gid
            );
            if (exists) return state;

            const withoutDuplicateGuest = state.temporaryAttachments.filter(
                a => !(a.kind === attachment.kind && guestId(a) === gid)
            );

            return {
                ...state,
                temporaryAttachments: [...withoutDuplicateGuest, attachment],
            };
        }

        case 'DETACH_ITEM': {
            const { hostId, guestId: gid } = action.payload;

            return {
                ...state,
                temporaryAttachments: state.temporaryAttachments.filter(
                    a => !(a.host_id === hostId && guestId(a) === gid)
                ),
            };
        }

        case 'CLEAR_TEMPORARY_ATTACHMENTS': {
            const hostId = action.payload?.hostId;

            if (hostId) {
                return {
                    ...state,
                    temporaryAttachments: state.temporaryAttachments.filter(a => a.host_id !== hostId),
                };
            }

            return {
                ...state,
                temporaryAttachments: [],
            };
        }

        case 'SET_ACTIVE_DRAG':
            return {
                ...state,
                activeDragId: action.payload,
            };

        default:
            return state;
    }
}
